import Config from '@config';
import { CACHE_EXPIRATION_TIME } from '@gasbi/shared/common/features/constants';
import {
  FiltersGrid,
  Pagination,
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@gasbi/shared/index';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { GasTariff, GasTariffRequestBody } from './types';

export const gasTariffsApi = createApi({
  reducerPath: 'gasTariffsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/gas-tariffs`,
  }),
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  tagTypes: ['GasTariff', 'GasTariffs'],
  endpoints: builder => ({
    getGasTariffI: builder.infiniteQuery<GasTariff[], FiltersGrid, Pagination>({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['GasTariff', 'GasTariffs'],
    }),
    getGasTariffById: builder.query<GasTariff, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['GasTariff'],
    }),
    createGasTariff: builder.mutation<GasTariff, GasTariffRequestBody>({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: result => [
        'GasTariffs',
        { type: 'GasTariff', id: result?.id },
      ],
    }),
    updateGasTariff: builder.mutation<GasTariff, GasTariffRequestBody>({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        'GasTariffs',
        { type: 'GasTariff', id: result?.id },
      ],
    }),
    deleteGasTariffById: builder.mutation<void, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => ['GasTariffs', { type: 'GasTariff', id }],
    }),
  }),
});

export const {
  useGetGasTariffByIdQuery,
  useCreateGasTariffMutation,
  useUpdateGasTariffMutation,
  useDeleteGasTariffByIdMutation,
} = gasTariffsApi;

export const useGetGasTariffIInfiniteQuery = normalizeInfiniteQueryData(
  gasTariffsApi.useGetGasTariffIInfiniteQuery,
);
